import { FC, Fragment, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  formControlLabelClasses,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  styled,
} from '@mui/material';
import { v4 } from 'uuid';

import { createCollection, saveCollection } from '@api/editorialLayout/collectionAdmin';
import { Button, ContentIcon, Loading, TextField } from '@components/base';
import LayoutEntityConflictSection from '@components/content/LayoutEntity/LayoutEntityConflictSection';
import LayoutEntityConflictWarning from '@components/content/LayoutEntity/LayoutEntityConflictWarning';
import { CollectionType } from '@enums/CollectionType';
import useGenericDataContext from '@hooks/useGenericDataContext';
import useSnack from '@hooks/useSnack';
import useValidation from '@hooks/useValidation';
import ValidationProvider from '@hooks/useValidation/provider';
import Collection, { CollectionInfo } from '@models/Collection';
import LocalizedFieldset from '@modules/content/components/LocalizedFieldset';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { SaveDialogContext } from '@utils/saveUtils';
import { collectionFieldNames, isValidationError } from '@utils/validationErrorUtils';
import { getCollectionListPath, getCollectionPath } from 'src/appRoutes';

import { useCollectionQuery } from '../../hooks/useCollectionQuery';

import TagField from './TagField';

export type SaveCollectionDialogProps = {
  collection?: Partial<Collection> & CollectionInfo;
  error?: ReactNode;
  hideTypeField?: boolean;
  loading?: boolean;
  onClose: () => void;
  onSuccess?: (collection: Collection) => void;
  saveContext: SaveDialogContext;
};

const StyledFormControlLabel = styled(FormControlLabel)`
  .${formControlLabelClasses.asterisk} {
    display: none;
  }
`;

const SaveCollectionDialogContent: FC<SaveCollectionDialogProps> = ({
  collection,
  error,
  hideTypeField,
  loading,
  onClose,
  onSuccess,
  saveContext,
}) => {
  const { addSnack } = useSnack();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const location = useLocation();

  const [isSaving, setIsSaving] = useState(false);

  const [collectionDraft, setCollectionDraft] = useState<Collection>({
    id: '',
    data: {},
    type: -1,
    name: { da: '', no: '', en: '' },
    description: '',
    slug: '',
    tags: [],
    ...collection,
  } as Collection);

  const [isMounted, setIsMounted] = useState(saveContext === SaveDialogContext.CREATE);

  const saveExistingCollection =
    saveContext === SaveDialogContext.SAVE || saveContext === SaveDialogContext.SAVE_AND_CLOSE;

  const { data: { updatedDateTime } = {}, dataUpdatedAt } = useGenericDataContext<Collection>();

  const queryKey = useMemo(() => v4(), []);

  const {
    data: freshCollection,
    isLoading: isLoadingFreshCollection,
    refetch,
    dataUpdatedAt: freshDataUpdatedAt,
  } = useCollectionQuery(collection?.id ?? '', { enabled: saveExistingCollection }, queryKey);

  const hasConflict =
    saveExistingCollection &&
    dataUpdatedAt < freshDataUpdatedAt &&
    updatedDateTime &&
    freshCollection?.updatedDateTime &&
    updatedDateTime < freshCollection.updatedDateTime;

  useEffect(() => {
    if (!isMounted && (saveContext === SaveDialogContext.CREATE || collection)) setIsMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionDraft]);

  useEffect(() => setCollectionDraft(prev => ({ ...prev, ...collection })), [collection]);

  const { id, data, name, slug, description, tags, type } = collectionDraft;
  const { searchQuery } = useSearchAndFilter();
  const { setValidationErrors, validationErrorMap: { fieldErrors = {} } = {} } = useValidation();

  const performSave = (curSaveContext: SaveDialogContext | undefined = saveContext) => {
    setIsSaving(true);

    const saveExistingCollection =
      curSaveContext === SaveDialogContext.SAVE ||
      curSaveContext === SaveDialogContext.SAVE_AND_CLOSE;

    const saveHandler = saveExistingCollection ? saveCollection : createCollection;

    setIsSaving(false);

    saveHandler({
      ...collectionDraft,
      ...({
        data:
          data ||
          (collectionDraft.type === CollectionType.CHERRY_PICK ? { items: [] } : searchQuery),
      } as CollectionInfo),
      id,
    })
      .then(({ data, originalError, ok }) => {
        if (!data) throw new Error();

        if (isValidationError(data)) {
          setValidationErrors(data);
          return;
        }

        if (originalError) throw originalError;
        else if (!ok) throw new Error();
        else {
          switch (curSaveContext) {
            case SaveDialogContext.CREATE:
              addSnack(t('collectionCreated'), { variant: 'success' });
              navigate(getCollectionPath(data.id));
              break;
            case SaveDialogContext.DUPLICATE:
              addSnack(t('collectionCreated'), {
                actions: [
                  {
                    label: t('view'),
                    leftIcon: 'openInNew',
                    to: getCollectionPath(data.id),
                    ...{ target: '_blank' },
                  },
                ],
              });
              onSuccess?.(data);
              navigate(location, { state: { highlight: data.id } });
              break;
            case SaveDialogContext.SAVE_AS_COPY:
              addSnack(t('collectionCreated'));
              onSuccess?.(data);
              navigate(getCollectionPath(data.id));
              break;
            case SaveDialogContext.SAVE:
            case SaveDialogContext.SAVE_AND_CLOSE:
              addSnack(t('collectionSaved'), { variant: 'success' });
              onSuccess?.(data);

              if (curSaveContext === SaveDialogContext.SAVE_AND_CLOSE) {
                navigate(getCollectionListPath(), {
                  state: { highlight: data.id },
                });
              }
          }
        }
      })
      .catch(err => {
        addSnack(err?.originalError ?? t('errorMessage'), { variant: 'error' });
      })
      .finally(() => setIsSaving(false));
  };

  const handleSubmit = async () => {
    if (
      collection?.id &&
      (saveContext === SaveDialogContext.SAVE || saveContext === SaveDialogContext.SAVE_AND_CLOSE)
    ) {
      setIsSaving(true);

      await refetch();
      if (freshCollection?.updatedDateTime && freshCollection.updatedDateTime === updatedDateTime)
        performSave();
      else setIsSaving(false);
    } else performSave();
  };

  const dialogTitleId = 'save-collection-dialog-title';
  const collectionTypeLabelId = 'collection-type-radio-label';

  return (
    <Dialog
      aria-labelledby={dialogTitleId}
      fullWidth
      maxWidth='md'
      onClose={isSaving ? undefined : onClose}
      open
    >
      <DialogTitle id={dialogTitleId} sx={{ pb: 0 }}>
        {t('collections.saveCollection.dialogTitle', {
          context: saveContext,
        })}
      </DialogTitle>

      {loading || isLoadingFreshCollection || !isMounted ? (
        <Box>
          <DialogContent>
            <Loading />
          </DialogContent>
        </Box>
      ) : error ? (
        <Stack>
          <DialogContent>{error}</DialogContent>

          <DialogActions>
            <Button onClick={onClose}>{t('cancel')}</Button>
          </DialogActions>
        </Stack>
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <DialogContent>
            <Stack spacing={3}>
              {hasConflict && <LayoutEntityConflictWarning entityType='collection' />}

              {!hideTypeField && saveContext === SaveDialogContext.CREATE && (
                <FormControl required>
                  <FormLabel id={collectionTypeLabelId}>Collection type</FormLabel>

                  <RadioGroup
                    aria-labelledby={collectionTypeLabelId}
                    name='type'
                    onChange={({ target: { value } }) =>
                      setCollectionDraft(
                        prev => ({ ...prev, type: value as CollectionType }) as Collection
                      )
                    }
                    row
                    value={type}
                  >
                    {Object.values(CollectionType).map(collectionType => (
                      <StyledFormControlLabel
                        control={<Radio />}
                        key={collectionType}
                        label={
                          <Box alignItems='center' display='flex' gap={0.5}>
                            <ContentIcon
                              contentType={collectionType}
                              fontSize='small'
                              hideTooltip
                            />
                            {t('collection.typeMinimized', { context: collectionType })}
                          </Box>
                        }
                        required
                        value={collectionType}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}

              <LocalizedFieldset
                onChangeValue={(value, languages, slug) =>
                  setCollectionDraft(prev => {
                    const updatedCollection = { ...prev };
                    languages.forEach(language => {
                      updatedCollection.name = { ...updatedCollection.name, [language]: value };
                    });
                    if (slug != null) updatedCollection.slug = slug;
                    return updatedCollection;
                  })
                }
                required
                slug={slug}
                values={name}
              />

              <TextField
                disabled={isSaving}
                error={!!fieldErrors?.description}
                fullWidth
                helperText={fieldErrors?.description as string}
                label={t('field.description.label')}
                multiline
                name='description'
                onChange={({ currentTarget: { value: description } }) =>
                  setCollectionDraft(prev => ({ ...prev, description }))
                }
                required
                value={description}
              />

              <TagField
                disabled={isSaving}
                onChange={(_, tags) =>
                  setCollectionDraft(prev => ({ ...prev, tags: Array.from(tags) }))
                }
                value={tags}
              />

              {hasConflict && (
                <Fragment>
                  <LayoutEntityConflictSection
                    context='save'
                    disabled={isSaving}
                    entityType='collection'
                    handleSave={() =>
                      performSave(
                        saveContext === SaveDialogContext.SAVE_AND_CLOSE
                          ? SaveDialogContext.SAVE_AND_CLOSE
                          : SaveDialogContext.SAVE
                      )
                    }
                  />

                  <LayoutEntityConflictSection
                    context='saveCopy'
                    disabled={isSaving}
                    entityType='collection'
                    handleSave={() => performSave(SaveDialogContext.SAVE_AS_COPY)}
                  />
                </Fragment>
              )}
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button disabled={isSaving} onClick={onClose}>
              {t('cancel')}
            </Button>

            {!hasConflict && (
              <Button loading={isSaving} type='submit' variant='contained'>
                {t(saveContext.toString())}
              </Button>
            )}
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

const SaveCollectionDialog: FC<SaveCollectionDialogProps> = props => (
  <ValidationProvider fieldNames={collectionFieldNames}>
    <SaveCollectionDialogContent {...props} />
  </ValidationProvider>
);

export default SaveCollectionDialog;
